<template>
  <!-- StwButton -->
  <Button
    v-bind="$attrs"
    :size="primeSize"
    :rounded="rounded"
    :style="customStyle"
    :pt="{
      root: baseRootClass,
      label: 'font-medium',
    }"
  >
    <slot />
  </Button>
</template>

<script setup lang="ts">
import { Button } from 'primevue';
import { computed } from 'vue';

interface Props {
  size?: 'xs' | 'sm' | 'md' | 'lg';
  rounded?: boolean;
  color?: string; // 'default' for neutral theme; CSS color value for background overrides
  colorClasses?: string; // utility class string override for full button styling
}

const props = withDefaults(defineProps<Props>(), {
  size: 'sm',
  rounded: true,
  color: 'default',
  colorClasses: '',
});

const primeSize = computed(() => {
  if (props.size === 'lg') return 'large';
  if (props.size === 'xs') return 'small';
  // default: make small to achieve compact UI
  return 'small';
});

const baseRootClass = computed(() => {
  // Simplified sizing - all buttons use consistent min-height
  const sizeClass = 'px-3 py-1.5 text-sm min-h-[2.5rem]';
  const radiusClass = props.rounded ? 'rounded-xl' : 'rounded-md';

  const sharedStyle = [
    'duration-0',
    'flex items-center justify-center gap-2',
    '!border-0',
  ].join(' ');

  const hasCustomColor = Boolean(props.color && props.color !== 'default');
  const colorClasses = props.colorClasses
    ? props.colorClasses
    : hasCustomColor
      ? 'hover:brightness-90 focus-visible:brightness-90'
      : '!bg-surface-700/70 !text-surface-0 hover:!bg-white/25 focus-visible:!bg-white/25 focus-visible:!ring-2 focus-visible:!ring-white/20';
  const disabledClasses =
    props.colorClasses || hasCustomColor
      ? 'disabled:opacity-60 disabled:cursor-not-allowed'
      : 'disabled:!bg-surface-800/40 disabled:!text-surface-500';

  return [
    sizeClass,
    radiusClass,
    sharedStyle,
    colorClasses,
    disabledClasses,
  ].join(' ');
});

const customStyle = computed(() => {
  if (props.color && props.color !== 'default' && !props.colorClasses) {
    return { backgroundColor: props.color, color: 'white' } as const;
  }
  return undefined;
});
</script>

<style scoped></style>
